import React, { useState } from 'react';
import { destinations } from '../mock';
import { MapPin, Plane } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Destinations = () => {
  const [filter, setFilter] = useState('all');
  const { t } = useLanguage();

  const filteredDestinations = filter === 'all'
    ? destinations
    : destinations.filter(dest => dest.category === filter);

  return (
    <section id="destinations" className="py-24 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('ourDestinations')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('destinationsSubtitle')}
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              filter === 'all'
                ? 'bg-blue-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-blue-50 shadow-md'
            }`}
          >
            {t('all')}
          </button>
          <button
            onClick={() => setFilter('national')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              filter === 'national'
                ? 'bg-blue-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-blue-50 shadow-md'
            }`}
          >
            {t('national')}
          </button>
          <button
            onClick={() => setFilter('african')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              filter === 'african'
                ? 'bg-blue-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-blue-50 shadow-md'
            }`}
          >
            {t('african')}
          </button>
          <button
            onClick={() => setFilter('international')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              filter === 'international'
                ? 'bg-blue-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-blue-50 shadow-md'
            }`}
          >
            {t('international')}
          </button>
        </div>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDestinations.map((destination) => (
            <div
              key={destination.id}
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 cursor-pointer"
            >
              {/* Image or Gradient */}
              <div className="aspect-[4/3] overflow-hidden relative">
                {destination.image ? (
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${destination.gradient || 'from-blue-600 to-blue-900'} flex items-center justify-center group-hover:scale-110 transition-transform duration-700`}>
                    <div className="text-center">
                      <div className="text-7xl mb-2 drop-shadow-lg" role="img" aria-label={destination.country}>
                        {destination.flag}
                      </div>
                      <Plane className="text-white/30 mx-auto" size={48} />
                    </div>
                  </div>
                )}
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={18} className="text-blue-400" />
                  <span className="text-sm text-blue-300 font-medium">{destination.country}</span>
                </div>
                <h3 className="text-2xl font-bold">{destination.name}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Destinations;
